import { useEffect, useState } from 'react';

export interface ConfirmScriptExecutionModalProps {
  isOpen: boolean;
  databaseName: string;
  environmentName: string;
  scriptFilename: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmScriptExecutionModal({
  isOpen,
  databaseName,
  environmentName,
  scriptFilename,
  onConfirm,
  onCancel,
}: ConfirmScriptExecutionModalProps) {
  const [confirmed, setConfirmed] = useState(false);

  // Reset checkbox state when modal opens or closes
  useEffect(() => {
    setConfirmed(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-script-execution-title"
    >
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      <div className="relative w-full max-w-md mx-4 rounded-lg bg-surface p-6 shadow-xl border border-border">
        <h2
          id="confirm-script-execution-title"
          className="text-lg font-semibold text-content"
        >
          Confirm Script Execution
        </h2>

        <div className="mt-4 space-y-3">
          <dl className="text-sm space-y-2">
            <div className="flex gap-2">
              <dt className="font-medium text-content-secondary">Database:</dt>
              <dd className="text-content">{databaseName}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-content-secondary">Environment:</dt>
              <dd className="text-content">{environmentName}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="font-medium text-content-secondary">Script:</dt>
              <dd className="text-content font-mono text-xs break-all">{scriptFilename}</dd>
            </div>
          </dl>

          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              ⚠ This script may make irreversible changes to the database.
            </p>
          </div>
        </div>

        <label className="mt-4 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border text-amber-600 focus:ring-amber-500"
          />
          <span className="text-sm text-content-secondary">
            I understand this script will run against the live database
          </span>
        </label>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-md text-content-secondary bg-surface border border-border hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 min-h-[44px]"
          >
            Confirm &amp; Execute
          </button>
        </div>
      </div>
    </div>
  );
}
